import { Portal } from '@gorhom/portal';
import { BroadcastChannel } from 'broadcast-channel';
import React, { useEffect } from 'react';
import {
  StyleSheet,
  View,
  TouchableOpacity,
  Platform,
  TouchableWithoutFeedback,
} from 'react-native';
import { useMediaQuery } from 'react-responsive';

import {
  ONBOARDING_SLIDE,
  SELECTED_METHOD,
  FIREBASE_MOBILE_APP_URL,
  VERIFY_OPTION,
  EKYC_BRODCAST_CHANNEL,
  EKYC_BRODCAST_MESSAGE,
} from 'shared/constants/ekyc';
import Backdrop from 'shared/modules/common/components/atoms/Backdrop';
import Icon from 'shared/modules/common/components/atoms/Icon';
import generateID from 'shared/modules/common/utils/generate-id';
import AskToVerifyStage from 'shared/modules/ekyc/components/organism/AskToVerifyStage';
import InfoStage from 'shared/modules/ekyc/components/organism/InfoStage';
import QRCodeStage from 'shared/modules/ekyc/components/organism/QRCodeStage';
import SelectMethodStage from 'shared/modules/ekyc/components/organism/SelectMethodStage';
import { ITEM, SCREEN } from 'shared/modules/ekyc/constants/mixpanel';
import {
  mixpanelTrackClickEvent,
  mixpanelStartTrackPageViewDurationEvent,
  mixpanelStopTrackPageViewDurationEvent,
} from 'shared/modules/ekyc/utils/kyc-tracking-events';

type Props = {
  open?: boolean;
  onCancel?: () => void;
  onOpen?: () => void;
  onCloseButtonPress?: () => void;
  onClose?: () => void;
};

const EKYCOnboardingDialog = (props: Props) => {
  // Mount the dialog only when opened
  const [isMounted, setIsMounted] = React.useState(false);
  // The close button is automatically focused when Dialog is opened
  const closeButtonRef = React.useRef<TouchableOpacity>(null);
  const originalFocusedElement = React.useRef<{
    focusedEl: HTMLElement | null;
  }>({
    focusedEl: null,
  });
  const isBigScreen = useMediaQuery({ minWidth: 768 });
  const [currentSlide, setCurrentSlide] = React.useState(
    ONBOARDING_SLIDE.VERIFY_STAGE
  );
  const [verifyOption, setVerifyOption] = React.useState(VERIFY_OPTION.NOW);
  const [selectedMethod, setSelectedMethod] = React.useState(
    SELECTED_METHOD.MOBILE_APP
  );
  let bc: BroadcastChannel;

  const handleOnBackPress = () => {
    if (currentSlide === ONBOARDING_SLIDE.VERIFY_STAGE) {
      props.onCancel?.();
    } else if (currentSlide === ONBOARDING_SLIDE.INFO_STAGE) {
      setCurrentSlide(ONBOARDING_SLIDE.VERIFY_STAGE);
    } else if (currentSlide === ONBOARDING_SLIDE.SELECT_STAGE) {
      setCurrentSlide(ONBOARDING_SLIDE.INFO_STAGE);
    } else if (currentSlide === ONBOARDING_SLIDE.QR_CODE_STAGE) {
      setCurrentSlide(ONBOARDING_SLIDE.SELECT_STAGE);
    }
  };

  const handleOnPressVerifyStage = () => {
    mixpanelTrackClickEvent(SCREEN.WEB_KYC_ONBOARDING, {
      Item:
        verifyOption === VERIFY_OPTION.LATER
          ? ITEM.WEB_EKYC_ONBOARDING_NO_VERIFY
          : ITEM.WEB_EKYC_ONBOARDING_VERIFY,
    });

    if (verifyOption === VERIFY_OPTION.LATER) {
      props.onCancel?.();

      return;
    }

    setCurrentSlide(ONBOARDING_SLIDE.INFO_STAGE);
  };

  const handleOnPressInfoStage = () => {
    mixpanelTrackClickEvent(SCREEN.WEB_KYC_ONBOARDING, {
      Item: ITEM.WEB_EKYC_ONBOARDING_EKYC_INFO,
    });

    setCurrentSlide(ONBOARDING_SLIDE.SELECT_STAGE);
  };

  const handleOnPressSelectedMethod = () => {
    if (selectedMethod === SELECTED_METHOD.MOBILE_APP) {
      mixpanelTrackClickEvent(SCREEN.WEB_KYC_ONBOARDING, {
        Item: ITEM.WEB_EKYC_ONBOARDING_PICK_LOCATION_APP,
      });

      if (isBigScreen) {
        setCurrentSlide(ONBOARDING_SLIDE.QR_CODE_STAGE);
      } else {
        window.location.assign(FIREBASE_MOBILE_APP_URL);
      }
    } else if (selectedMethod === SELECTED_METHOD.MOBILE_WEB) {
      mixpanelTrackClickEvent(SCREEN.WEB_KYC_ONBOARDING, {
        Item: ITEM.WEB_EKYC_ONBOARDING_PICK_LOCATION_MOBILE_WEB,
      });

      if (isBigScreen) {
        setCurrentSlide(ONBOARDING_SLIDE.QR_CODE_STAGE);
      } else {
        window.open(`${process.env.MY_PAIDY_URL}/ekyc`, '_blank');
      }
    } else if (selectedMethod === SELECTED_METHOD.DESKTOP) {
      mixpanelTrackClickEvent(SCREEN.WEB_KYC_ONBOARDING, {
        Item: ITEM.WEB_EKYC_ONBOARDING_PICK_LOCATION_DESKTOP,
      });

      window.open(`${process.env.MY_PAIDY_URL}/ekyc`, '_blank');
    }
  };

  const handleOnCloseQRCode = () => {
    mixpanelTrackClickEvent(SCREEN.WEB_KYC_ONBOARDING, {
      Item: ITEM.WEB_EKYC_ONBOARDING_SCAN_QR_CODE,
    });

    props.onCancel?.();
  };

  const resetToDefault = () => {
    setCurrentSlide(ONBOARDING_SLIDE.VERIFY_STAGE);
    setVerifyOption(VERIFY_OPTION.NOW);
    setSelectedMethod(SELECTED_METHOD.MOBILE_APP);
  };

  const onClose = () => {
    // Reset to default
    resetToDefault();

    setIsMounted(false);
    // Return the focus to the element before the Dialog is opened when it is closed
    originalFocusedElement.current.focusedEl?.focus();
  };

  const onOpen = () => {
    // Reset back to default slider
    resetToDefault();

    // Automatically focus on the close button
    closeButtonRef.current?.focus();
  };

  const renderSlide = () => {
    if (currentSlide === ONBOARDING_SLIDE.VERIFY_STAGE) {
      return (
        <AskToVerifyStage
          onNextButtonPress={handleOnPressVerifyStage}
          selectedOption={verifyOption}
          onOptionSelect={(selectedOption) =>
            setVerifyOption(selectedOption as number)
          }
          onFocus={() =>
            mixpanelStartTrackPageViewDurationEvent(SCREEN.WEB_KYC_ONBOARDING)
          }
          onBlur={() =>
            mixpanelStopTrackPageViewDurationEvent(SCREEN.WEB_KYC_ONBOARDING, {
              slide: ITEM.WEB_EKYC_ONBOARDING_SLIDE + '1',
            })
          }
        />
      );
    } else if (currentSlide === ONBOARDING_SLIDE.INFO_STAGE) {
      return (
        <InfoStage
          onNextButtonPress={handleOnPressInfoStage}
          onFocus={() =>
            mixpanelStartTrackPageViewDurationEvent(SCREEN.WEB_KYC_ONBOARDING)
          }
          onBlur={() =>
            mixpanelStopTrackPageViewDurationEvent(SCREEN.WEB_KYC_ONBOARDING, {
              slide: ITEM.WEB_EKYC_ONBOARDING_SLIDE + '2',
            })
          }
        />
      );
    } else if (currentSlide === ONBOARDING_SLIDE.SELECT_STAGE) {
      return (
        <SelectMethodStage
          onNextButtonPress={handleOnPressSelectedMethod}
          selectedMethod={selectedMethod}
          onSelectMethod={(selected) => setSelectedMethod(selected as number)}
          onFocus={() =>
            mixpanelStartTrackPageViewDurationEvent(SCREEN.WEB_KYC_ONBOARDING)
          }
          onBlur={() =>
            mixpanelStopTrackPageViewDurationEvent(SCREEN.WEB_KYC_ONBOARDING, {
              slide: ITEM.WEB_EKYC_ONBOARDING_SLIDE_PICK_LOCATION,
            })
          }
        />
      );
    } else if (currentSlide === ONBOARDING_SLIDE.QR_CODE_STAGE) {
      return (
        <QRCodeStage
          onNextButtonPress={handleOnCloseQRCode}
          selectedMethod={selectedMethod}
          onFocus={() =>
            mixpanelStartTrackPageViewDurationEvent(SCREEN.WEB_KYC_ONBOARDING)
          }
          onBlur={() =>
            mixpanelStopTrackPageViewDurationEvent(SCREEN.WEB_KYC_ONBOARDING, {
              slide: ITEM.WEB_EKYC_ONBOARDING_SLIDE_SCAN_QR_CODE,
            })
          }
        />
      );
    } else {
      return (
        <AskToVerifyStage
          onNextButtonPress={handleOnPressVerifyStage}
          selectedOption={verifyOption}
          onOptionSelect={(selectedOption) =>
            setVerifyOption(selectedOption as number)
          }
          onFocus={() =>
            mixpanelStartTrackPageViewDurationEvent(SCREEN.WEB_KYC_ONBOARDING)
          }
          onBlur={() =>
            mixpanelStopTrackPageViewDurationEvent(SCREEN.WEB_KYC_ONBOARDING, {
              slide: ITEM.WEB_EKYC_ONBOARDING_SLIDE + '1',
            })
          }
        />
      );
    }
  };

  /** Enables scrolling for slides thats doesn't have sticky button */
  const canScroll = () => {
    if (isBigScreen) {
      return false;
    }

    if (
      currentSlide === ONBOARDING_SLIDE.SELECT_STAGE ||
      currentSlide === ONBOARDING_SLIDE.QR_CODE_STAGE
    ) {
      return true;
    }

    return false;
  };

  useEffect(() => {
    if (props.open) {
      setIsMounted(true);

      // Get the last focused element before Dialog is opened
      // When we close the Dialog the focus should be returned back to that element
      if (Platform.OS === 'web') {
        originalFocusedElement.current.focusedEl = document.activeElement as HTMLElement;
      }
    }
  }, [props.open]);

  useEffect(() => {
    /**
     * These code snippets serves as a listener which navigate to account page
     * when EKYC Application is completed.
     */
    bc = new BroadcastChannel(EKYC_BRODCAST_CHANNEL);

    bc.onmessage = (msg) => {
      if (msg === EKYC_BRODCAST_MESSAGE.NAVIGATE_TO_ACCOUNT) {
        onClose?.();
        window.location.assign(`${process.env.MY_PAIDY_URL}/account`);
      }
    };

    return () => {
      bc.close();
    };
  }, []);

  if (!isMounted) {
    return null;
  }

  return (
    <Portal>
      <Backdrop
        style={[styles.backdrop, !isBigScreen && { padding: 0 }]}
        open={props.open}
        onOpen={onOpen}
        onClose={onClose}
        onPress={() => props.onCancel?.()}
        id={'ekyc-onboarding-backdrop'}
      >
        <TouchableWithoutFeedback onPress={(e) => e.stopPropagation()}>
          <View
            style={[
              styles.root,
              !isBigScreen && styles.rootMobile,
              canScroll() && { overflow: 'scroll' },
            ]}
          >
            <View style={styles.header}>
              <View style={[styles.headerControl, styles.headerControlLeft]}>
                <TouchableOpacity
                  onPress={() => handleOnBackPress()}
                  /** Generate HTML and Test IDs  */
                  {...generateID('ekyc-onboarding-back-button')}
                  /** Platform-specific props */
                  {...Platform.select({
                    web: {
                      accessibilityRole: 'button',
                    },
                  })}
                >
                  <Icon name="back" size="24" />
                </TouchableOpacity>
              </View>
              <View style={[styles.headerControl, styles.headerControlRight]}>
                <TouchableOpacity
                  ref={closeButtonRef}
                  onPress={() => props.onCancel?.()}
                  /** Generate HTML and Test IDs  */
                  {...generateID('ekyc-onboarding-close-button')}
                  /** Platform-specific props */
                  {...Platform.select({
                    web: {
                      accessibilityRole: 'button',
                    },
                  })}
                >
                  <Icon name="close" size="24" />
                </TouchableOpacity>
              </View>
            </View>
            {renderSlide()}
          </View>
        </TouchableWithoutFeedback>
      </Backdrop>
    </Portal>
  );
};

const styles = StyleSheet.create({
  backdrop: {
    flex: 1,
    alignItems: 'center',
    padding: 24,
    overflow: 'scroll',
  },
  root: {
    padding: 24,
    width: '100%',
    maxWidth: 424,
    backgroundColor: '#F6F8FA',
    borderRadius: 8,
    minHeight: 700,
    overflow: 'hidden',
  },
  rootMobile: {
    minHeight: 'auto',
    height: '100%',
    maxHeight: '100svh',
    borderRadius: 0,
  },
  header: {
    marginBottom: 24,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    zIndex: 1,
  },
  headerControl: {
    width: 24,
    height: 24,
  },
  headerControlLeft: {
    marginRight: 24,
  },
  headerControlRight: {
    marginLeft: 24,
  },
});

export default EKYCOnboardingDialog;
